import { TimesheetBalanceFragment } from 'generated/resources/graphql'
import { chain, filter, flatMap, groupBy, max, maxBy, sum, sumBy } from 'lodash'
import moment from 'moment'

import { stringToDate } from 'modules/core/lib/dates'

export type DailyBalanceAggregateType = {
  balanceFromPlanning: number
  balanceFromContract: number
  balance: number
}

type AttendanceBalancePool = TimesheetBalanceFragment['balancePools'][number]
type AttendanceTimeBlock = TimesheetBalanceFragment['balancePools'][number]['transfers'][number]

export type AggregateBalanceByAccumulation = {
  updatedAt: string
  startOn: string
  endOn: string
  balancePools: AttendanceBalancePool[]
  dailyBalance: number
  dailyBalanceFromContract: number
  dailyBalanceFromPlanning: number
  employeeId: number
  id: string
}

class BalanceUtils {
  static roundedBalanceMinutes = (dailyBalance = '0') => {
    return Math.round(Number(dailyBalance))
  }

  static aggregateBalanceByAccumulation(
    balances: TimesheetBalanceFragment[],
    employeeId: number
  ): AggregateBalanceByAccumulation[] {
    if (!balances.length) return []

    const emptyTimeBlock: AttendanceTimeBlock = { sourcePoolType: 'balance' }
    const today = moment()
    return chain(balances)
      .sortBy('accumulationStartOn')
      .groupBy('accumulationStartOn')
      .toPairs()
      .map(([startOn, group]) => {
        const endOn = max(group.map((b) => b.accumulationEndOn)) || ''

        const dailyBalance = sum(group.map((b) => Number(b.dailyBalance)))
        const dailyBalanceFromPlanning = sum(group.map((b) => Number(b.dailyBalanceFromPlanning)))
        const dailyBalanceFromContract = sum(group.map((b) => Number(b.dailyBalanceFromContract)))

        const maxUpdatedAt = maxBy(group, 'updatedAt')
        const updatedAt = maxUpdatedAt ? maxUpdatedAt.updatedAt : ''

        const allPools = flatMap(
          filter(group, (b) => moment(b.accumulationEndOn).isBefore(today)),
          (b) => filter(b.balancePools, (p) => p.usages.length > 0 || p.transfers.length > 0)
        )

        const balancePools: AttendanceBalancePool[] = flatMap(
          Object.values(groupBy(allPools, 'type')),
          (pools) => {
            const allUsages = flatMap(pools, (pool) => pool.usages ?? [])
            const allTransfers = flatMap(pools, (pool) => pool.transfers ?? [])

            const groupedUsages: AttendanceTimeBlock[] = Object.values(
              groupBy(allUsages, '_uniqueKey')
            ).map((usageGroup) => {
              const baseUsage = {
                ...usageGroup[0],
                sourcePoolType: usageGroup[0]?.sourcePoolType || emptyTimeBlock.sourcePoolType,
              }
              const usageMinutes = sumBy(usageGroup, (u) => Number(u?.minutes ?? 0))
              return {
                ...baseUsage,
                minutes: usageMinutes.toString(),
                __typename: 'AttendanceTimeBlock',
              }
            })

            const groupedTransfers: AttendanceTimeBlock[] = Object.values(
              groupBy(allTransfers, '_uniqueKey')
            ).map((transferGroup) => {
              const baseTransfer = {
                ...transferGroup[0],
                sourcePoolType: transferGroup[0]?.sourcePoolType || emptyTimeBlock.sourcePoolType,
              }
              const transferMinutes = sumBy(transferGroup, (t) => Number(t?.minutes ?? 0))
              return {
                ...baseTransfer,
                minutes: transferMinutes.toString(),
                __typename: 'AttendanceTimeBlock',
              }
            })

            return {
              type: pools[0]?.type ?? 'balance',
              transfers: groupedTransfers,
              usages: groupedUsages,
              __typename: 'AttendanceBalancePool',
            }
          }
        )

        return {
          startOn,
          endOn,
          updatedAt,
          balancePools,
          dailyBalance,
          dailyBalanceFromPlanning,
          dailyBalanceFromContract,
          employeeId,
          id: `${employeeId}_${startOn}_${endOn}`,
        }
      })
      .value()
  }

  static sumDailyBalance(
    balances: Pick<
      TimesheetBalanceFragment,
      'date' | 'dailyBalanceFromPlanning' | 'dailyBalanceFromContract' | 'dailyBalance'
    >[],
    startDate: string,
    endDate: string,
    adjustedDailyMinutes?: boolean
  ): DailyBalanceAggregateType {
    const startMoment = stringToDate(startDate, 'backend')
    const endMoment = stringToDate(endDate, 'backend')

    const balanceRangeStartOn = moment().isBefore(startMoment) ? moment() : startMoment
    const balanceRangeEndOn = moment().isBefore(endMoment) ? moment() : endMoment

    // Determine end date for contract balance calculation
    // If adjustedDailyMinutes is true, use end of week of endMoment
    // Otherwise use the regular balanceRangeEndOn
    const contractBalanceEndDate = adjustedDailyMinutes
      ? moment(endMoment).endOf('week')
      : balanceRangeEndOn

    const maxEndDate = moment.max(balanceRangeEndOn, contractBalanceEndDate)

    const filteredBalances = balances.filter((balance) =>
      stringToDate(balance.date, 'backend').isBetween(balanceRangeStartOn, maxEndDate, 'day', '[]')
    )

    return filteredBalances.reduce(
      (total, balance) => {
        const balanceDate = stringToDate(balance.date, 'backend')

        // Always add to planning and total balance if within normal date range
        if (balanceDate.isBetween(balanceRangeStartOn, balanceRangeEndOn, 'day', '[]')) {
          total.balanceFromPlanning += Number(balance.dailyBalanceFromPlanning)
          total.balance += Number(balance.dailyBalance)
        }

        // For contract balance, use different end date when adjustedDailyMinutes is true
        if (balanceDate.isBetween(balanceRangeStartOn, contractBalanceEndDate, 'day', '[]')) {
          total.balanceFromContract += Number(balance.dailyBalanceFromContract)
        }

        return total
      },
      { balanceFromPlanning: 0, balanceFromContract: 0, balance: 0 }
    )
  }
}

export default BalanceUtils
